"use client";

import { CheckCircleIcon as FilledIcon } from "@heroicons/react/20/solid";
import { CheckCircleIcon as OutlineIcon } from "@heroicons/react/24/outline";
import NoSsr from "@mui/material/NoSsr";
import { ComponentProps, Fragment } from "react";
import { twMerge } from "tailwind-merge";

import { HTMLMarkdown } from "@/components/markdown/HTMLMarkdown";
import { ChargeDuration } from "@/features/stock-mining-pricing/data/duration";

import { getCardButton } from "../../data/get-card-button";
import {
  PlanContentItem,
  PricingInfo,
  TiersData,
} from "../../types/pricing-tiers";
import { CurrentPlanChip } from "./CurrentPlanChip";

type PricingCardsProps = {
  data: TiersData;
  duration: ChargeDuration;
};

export const PricingCards = ({ data, duration }: PricingCardsProps) => {
  const currentData = data[duration]?.filter((item) => !item.disabled);
  const durationText = duration === "monthly" ? "Monthly" : "Yearly";

  return (
    <div
      className="relative grid grid-cols-1 gap-7 lg:grid-cols-3"
      aria-label="content of the program"
    >
      {currentData?.map((item, index) => {
        const isPremium = index === 2;
        const plan = item.plan ?? "free";
        return (
          <PricingCard
            key={item.name}
            aria-label={item.name}
            className={twMerge(isPremium && "border-sky-600")}
          >
            <div className="space-y-5">
              <div className="relative">
                <p
                  className={twMerge(
                    "font-semibold",
                    isPremium && "text-sky-700"
                  )}
                >
                  {item.name}
                </p>
                <CurrentPlanChip planNameText={item.name} duration={duration} />
              </div>
              <PricingInfoComponent className="h-[80px]" data={item.pricing} />
            </div>
            {getCardButton({ plan, durationText })}
            <div className="space-y-7">
              <LimitationText data={item.limitDesc} />
              <PricingContent key={item.name} data={item.content} />
            </div>
          </PricingCard>
        );
      })}
    </div>
  );
};

const PricingCard = (props: ComponentProps<"div">) => (
  <div
    {...props}
    className={twMerge(
      "mx-auto flex h-full w-full max-w-sm flex-col gap-y-7 rounded-3xl border-2 border-zinc-200 bg-white px-9 py-10",
      props.className
    )}
  >
    {props.children}
  </div>
);

const PricingInfoComponent = ({
  data,
  ...props
}: ComponentProps<"div"> & { data?: PricingInfo }) => {
  if (!data) return null;
  return (
    <div
      {...props}
      className={twMerge("relative flex flex-col gap-2.5", props.className)}
    >
      <p className="space-x-2">
        <span className="text-5xl font-bold">{`$${data.fee}`}</span>
        <span>
          {data.currency}/{data.billingPeriod}
        </span>
      </p>
      {data.description ? (
        <div
          className={twMerge(
            "text-sm font-semibold text-sky-600",
            data.fee === 0 && "invisible"
          )}
        >
          <NoSsr>
            <HTMLMarkdown>{data.description}</HTMLMarkdown>
          </NoSsr>
        </div>
      ) : null}
    </div>
  );
};

const LimitationText = ({
  data,
  ...props
}: ComponentProps<"p"> & { data?: string }) => {
  if (!data) return null;
  return (
    <p {...props} className={twMerge("font-semibold", props.className)}>
      {data}
    </p>
  );
};

const PricingContent = ({
  data,
  ...props
}: ComponentProps<"ul"> & { data?: PlanContentItem[] }) => {
  if (!data) return null;
  return (
    <ul {...props} className={twMerge("space-y-4", props.className)}>
      {data.map(({ name, style, nestedContent }, index) => (
        <Fragment key={`${name}-${index}`}>
          <li
            className={twMerge(
              "flex items-start gap-3 text-sm",
              style === "filled"
                ? "font-semibold text-sky-600"
                : "text-zinc-600"
            )}
          >
            {style === "filled" ? (
              <FilledIcon className="h-5 w-5 shrink-0 text-sky-600" />
            ) : (
              <OutlineIcon className="h-5 w-5 shrink-0 text-sky-700" />
            )}
            {name}
          </li>
          {nestedContent && nestedContent.length > 0 && (
            <PricingContent
              data={nestedContent}
              className="ml-8"
              key={`${name}-nested-${index}`}
            />
          )}
        </Fragment>
      ))}
    </ul>
  );
};
